import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import cx from 'classnames';

import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { FORM, FORM_CHECK_BOX, FORM_STEP, MODAL, SLIDE } from '../../../constants/widgetTypes';

import { FormContext, WidgetsContext } from '../core/context';
import { useFormInputs, useOtpFormInput } from '../core/hooks';
import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';
import { injectAktivFallback } from '../widget.helper';

import styles from './form.module.scss';
import get from 'lodash/get';
import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';


export const FormWidgetContext = React.createContext({
  updateField: () => { },
  fields: {},
  onFormSubmit: () => { },
  onOtpFormSubmit: () => { },
  onOtpResend: () => { }
});

const FormWidget = function FormEditor({ className, style, widgetProps, breakpoint, children }) {
  const context = React.useContext(WidgetsContext);
  const formContext = React.useContext(FormContext);
  const lastCalculatedModalHeightRef = React.useRef(null);
  const lastCalculatedFormHeightRef = React.useRef(null);

  const [state, setState] = useState({
    formStyles: {},
    step: ''
  });

  const [formContextState, setFormContextState] = useState({
    errors: {},
    fields: []
  })

  const selector = context.selector;
  const allWidgets = useSelector(state => get(state, `${selector}.widgets`) || []);
  const id = get(widgetProps, 'uid');
  const formSuccess = get(widgetProps, 'success', false);
  const otpSuccess = get(widgetProps, 'otpSuccess', false);
  const submitting = get(widgetProps, 'submitting', false);
  const formErrors = get(widgetProps, 'error');
  const formOtpId = get(widgetProps, 'formOtpId');
  const isOtpRequired = get(widgetProps, `properties.${breakpoint}.emailOtp`, false);

  const isProgressiveEnabled = get(context, 'progressiveGating.enabled', false);

  var formSteps = children.filter(item => get(item, 'props.widgetProps.type') === FORM_STEP)
    .map(item => get(item, 'props.widgetProps'));

  const inputWidgets = useFormInputs(id);
  const otpInputWidget = useOtpFormInput(id);

  const properties = get(widgetProps, 'properties', {});
  const deviceProperties = get(properties, `${breakpoint}`, {});

  Fonts.useFont(get(widgetProps, 'fonts') || []);

  useEffect(() => {
    formSteps = orderBy(formSteps, ['order'], ['asc']);
    const formStep = formSteps[0];

    setState(state => {
      return {
        ...state,
        step: get(widgetProps, 'step', get(formStep, 'uid', null))
      }
    })
  }, [get(widgetProps, 'step')]);

  useEffect(() => {
    setFormContextState(state => {
      return {
        ...state,
        updateField: handleUpdateField,
        onFormSubmit: handleFormSubmit,
        onOtpFormSubmit: handleOtpFormSubmit,
        onOtpResend: handleOtpResend,
        submitting: false
      }
    });
  }, []);

  useEffect(() => {
    if (formSuccess) {
      formSteps = orderBy(formSteps, ['order'], ['desc']);
      let formStep;

      if(formSteps?.length > 2){
        formStep = isOtpRequired ? formSteps[0] : formSteps[1];
      } else {
        formStep = formSteps[0];
      }
      
      setState(state => {
        return {
          ...state,
          step: get(widgetProps, 'step', get(formStep, 'uid', null))
        }
      })
    }
  }, [formSuccess, isOtpRequired]);

  useEffect(() => {
    if (otpSuccess) {
      formSteps = orderBy(formSteps, ['order'], ['desc']);
      const formStep = formSteps[1];

      setState(state => {
        return {
          ...state,
          step: get(widgetProps, 'step', get(formStep, 'uid', null))
        }
      })
    }
  }, [otpSuccess]);

  useEffect(() => {
    setFormContextState(state => {
      return {
        ...state,
        submitting: submitting
      }
    });
  }, [submitting]);

  useEffect(() => {
    setFormContextState((state) => {
      if (formErrors && formErrors.email) {
        const emailField = inputWidgets.find(widget => get(widget, 'properties.desktop.field') === 'email');
        if (emailField) {
          const { email, ...restErrors } = formErrors;
          return {
            ...state,
            errors: {
              ...restErrors,
              [emailField.uid]: email
            }
          };
        }
      }

      return {
        ...state,
        errors: formErrors || {}
      }
    });
  }, [formErrors]);

  useEffect(() => {
    const updateWidgetHeight = get(context, 'updateWidgetHeight');
    var formStyles = getStyles(properties, breakpoint);
    
    if (isProgressiveEnabled && state.step && updateWidgetHeight) {
      const currentStepWidget = allWidgets.find(w => w.uid === state.step);
      const currentStepName = get(currentStepWidget, 'name', '');
      
      if (currentStepWidget && currentStepName !== "Form") {
        const stepChildren = allWidgets.filter(w => w.parent === state.step);
                
        if (stepChildren.length > 0) {
          const childrenWithPositions = stepChildren.map(child => {
            const translate = get(child, `properties.${breakpoint}.layout.transform.translate`, '0px,0px');
            const y = parseFloat(translate.split(',')[1]);
            const height = get(child, `properties.${breakpoint}.layout.height`, 'auto');
            const childHeight = height === 'auto' ? 60 : parseFloat(height);
            
            const bottom = y + childHeight;
            return { bottom };
          });
          
          childrenWithPositions.sort((a, b) => a.bottom - b.bottom);
          const lastChild = childrenWithPositions[childrenWithPositions.length - 1];
          
          const bottomPadding = 30;
          const newFormHeight = lastChild.bottom + bottomPadding;
          
          if (lastCalculatedFormHeightRef.current !== newFormHeight) {
            lastCalculatedFormHeightRef.current = newFormHeight;
            
            updateWidgetHeight(context?.session, id, breakpoint, `${newFormHeight}px`);
          }
          
          formStyles.height = `${newFormHeight}px`;
        }
      }
    }
    
    setState(state => ({
      ...state,
      formStyles: formStyles
    }))
  }, [deviceProperties, state.step, isProgressiveEnabled, allWidgets, breakpoint, id, context?.session]);

  useEffect(() => {
    const updateWidgetHeight = get(context, 'updateWidgetHeight');
    
    if (!isProgressiveEnabled || !state.step || !updateWidgetHeight) {
      return;
    }
    
    const currentStepWidget = allWidgets.find(w => w.uid === state.step);
    const currentStepName = get(currentStepWidget, 'name', '');
    
    if (!currentStepWidget || currentStepName === "Form") {
      return;
    }
    
    const formWidget = allWidgets.find(w => w.uid === id);
    const formParent = get(formWidget, 'parent');
    
    if (!formParent) {
      return;
    }
    
    const modalWidget = allWidgets.find(w => w.uid === formParent);
    const modalType = get(modalWidget, 'type');
    
    if (modalType !== MODAL) {
      return;
    }
    
    const modalChildren = allWidgets.filter(w => w.parent === formParent);
    
    if (modalChildren.length === 0) {
      return;
    }
    
    const calculatedFormHeight = state.formStyles.height;
    
    const childrenWithPositions = modalChildren.map(child => {
      const translate = get(child, `properties.${breakpoint}.layout.transform.translate`, '0px,0px');
      const y = parseFloat(translate.split(',')[1]);
      
      const childType = get(child, 'type');
      let childHeight = 0;
      
      if (childType === FORM) {
        if (calculatedFormHeight) {
          childHeight = parseFloat(calculatedFormHeight);
        } else {
          const formHeight = get(child, `properties.${breakpoint}.layout.height`, 'auto');
          childHeight = formHeight === 'auto' ? 60 : parseFloat(formHeight);
        }
      } else {
        const height = get(child, `properties.${breakpoint}.layout.height`, 'auto');
        childHeight = height === 'auto' ? 60 : parseFloat(height);
      }
      
      const bottom = y + childHeight;
      return { bottom };
    });
    
    childrenWithPositions.sort((a, b) => a.bottom - b.bottom);
    const lastChild = childrenWithPositions[childrenWithPositions.length - 1];
    
    const bottomPadding = 30;
    const newModalHeight = Math.max(lastChild.bottom + bottomPadding, 500);
    
    if (lastCalculatedModalHeightRef.current !== newModalHeight) {
      lastCalculatedModalHeightRef.current = newModalHeight;
      updateWidgetHeight(context?.session, formParent, breakpoint, `${newModalHeight}px`);
    }
    
  }, [state.step, state.formStyles, isProgressiveEnabled, allWidgets, breakpoint, id, context?.session]);

  useEffect(() => {
    setFormContextState(state => {
      return {
        ...state,
        updateField: handleUpdateField,
        onFormSubmit: handleFormSubmit,
        onOtpFormSubmit: handleOtpFormSubmit,
        onOtpResend: handleOtpResend,
      }
    });
  }, [formContextState.fields]);

  var widgetStyle = {
    ...style,
    ...state.formStyles
  }

  const handleUpdateField = (uid, field, fieldId, value) => {
    setFormContextState(state => {
      var fields = state.fields || [];
      fields = fields.filter(item => item.uid != uid);
      fields = [...fields, {
        uid: uid,
        id: field,
        fieldId: fieldId,
        value: value
      }];

      let errors = omit(state.errors, uid);

      return {
        ...state,
        fields: fields,
        errors: errors
      }
    })
  }

  const filterValidFields = (fields = []) => {
    return fields.filter(field => {
      const value = field.value;
  
      if (value === null || value === undefined || value === '') {
        return false;
      }
  
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
  
      if (value === false) {
        return false;
      }
  
      return true;
    });
  };
  

  const handleFormSubmit = () => {
    const isProgressive = get(context, 'progressiveGating.enabled', false);
  
    let errors = validateForm();
  
    if (Object.keys(errors).length > 0) {
      setFormContextState(state => ({
        ...state,
        errors
      }));
      return;
    }
  
    const fieldsToSubmit = filterValidFields(formContextState.fields);
  
    formContext.onFormSubmit(context.session, id, {
      fields: fieldsToSubmit,
      parent: context.parent,
      eventUid: context.eventUid,
      gatingTrigger: context.gatingTrigger,
      smart: context.smart,
      progressive: isProgressive
    });
  };
  

  const handleOtpFormSubmit = () => {
    let errors = validateOtpForm();

    if (Object.keys(errors).length > 0) {
      setFormContextState(state => {
        return {
          ...state,
          errors: errors
        }
      });

      return;
    }

    const otpObject = formContextState?.fields?.find(item => item.id === "otp");
    const otp = otpObject?.value

    formContext.onOtpFormSubmit(context.session, id, {
      fields: formContextState.fields,
      parent: context.parent,
      formOtpId: formOtpId && formOtpId,
      otp: otp && otp,
      eventUid: context.eventUid,
      gatingTrigger: context.gatingTrigger,
      smart: context.smart
    });
  };

  const handleOtpResend = () => {
    formContext.onOtpResend(context.session, id, {
      formOtpId: formOtpId
    });
  };

  const validateForm = () => {
    var errors = {};
  
    const isProgressive = get(context, 'progressiveGating.enabled', false);
    const clusterFields = get(context, 'progressiveGating.clusterFields', []);
  
    if (isProgressive && clusterFields.length > 0) {
      const fieldsToValidate = (inputWidgets || []).filter(widget => {
        const contentId = get(widget, 'content.id', null);
        return contentId && clusterFields.includes(contentId);
      });
    
    fieldsToValidate.forEach(widget => {
      var field = (formContextState.fields || []).find(field => {
        return get(widget, 'uid', '') === field.uid;
      });

      var value = get(field, 'value', '');
      var uid = get(widget, 'uid', null);
      var properties = get(widget, 'properties.desktop', {});
      var mandatory = properties.mandatory;

      if (properties.field === 'email' && !validateEmail(value)) {
        errors[uid] = `Please provide a valid ${properties.label || 'value'}`;
        return;
      }

      if (mandatory && (!value || (Array.isArray(value) && value.length === 0) || value === false)) {
        if(get(widget, 'type') == FORM_CHECK_BOX){
          errors[uid] = `This field cannot be empty`;
          return;
        }
        errors[uid] = `Please provide a valid ${properties.label || 'value'}`;
      }
    });
    
    return errors;
  }
  
    (inputWidgets || []).forEach(widget => {
      var field = (formContextState.fields || []).find(field => {
        return get(widget, 'uid', '') === field.uid;
      });

      var value = get(field, 'value', '');
      var uid = get(widget, 'uid', null);
      var properties = get(widget, 'properties.desktop', {});
      var mandatory = properties.mandatory;

      if (properties.field === 'email' && !validateEmail(value)) {
        errors[uid] = `Please provide a valid ${properties.label || 'value'}`;
        return;
      }

      if (mandatory && (!value || (Array.isArray(value) && value.length === 0) || value === false)) {
        if(get(widget, 'type') == FORM_CHECK_BOX){
          errors[uid] = `This field cannot be empty`;
          return;
        }
        errors[uid] = `Please provide a valid ${properties.label || 'value'}`;
      }
    });

  return errors;
}

  const validateOtpForm = () => {
    var errors = {};
    (otpInputWidget || []).forEach(widget => {
      var field = (formContextState.fields || []).find(field => {
        return get(widget, 'uid', '') === field.uid;
      })

      var value = get(field, 'value', '');
      var uid = get(widget, 'uid', null);
      var properties = get(widget, 'properties.desktop', {});
      var mandatory = properties.mandatory;

      if (mandatory && properties.field === 'otp' && !validateOtp(value)) {
        errors[uid] = `Please provide a valid otp`;
        return
      }
    })

    return errors;
  }

  const validateEmail = (email) => {
    if (!email) {
      return false;
    }

    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
  }

  const validateOtp = (otp, requiredLength = 4) => {
    if (!otp) {
      return false;
    }
  
    const trimmedOtp = otp.trim();
  
    if (trimmedOtp.length !== requiredLength) {
      return false;
    }
  
    return trimmedOtp;
  };

  return (
    <FormWidgetContext.Provider value={formContextState}>
      <div id={id}
        className={cx(className, styles.form)}
        style={widgetStyle}
        data-widget-id={id}>
        {
          children.filter(item => get(item, 'props.widgetProps.uid') === state.step)
        }
      </div>
    </FormWidgetContext.Provider>
  )
};

/**
 * Get Styles for the properties
*/
function getStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = get(properties, `desktop`);
  const deviceProperties = get(properties, `${breakpoint}`);

  const font = get(defaultProperties, 'font', {});

  //font-color
  const colorRgb = get(font, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = get(font, 'fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: injectAktivFallback(fontFamily)
  }

  //font-size
  const fontSizeString = get(deviceProperties, 'regular.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;


  //padding
  const paddingString = get(deviceProperties, 'padding', null)
  var padding = '20'
  if (paddingString) {
    padding = parseInt(paddingString) + 'px';
  }

  styles['padding'] = padding;


  //font-style
  const bold = get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //background
  const backgroundRgb = get(defaultProperties, 'background', null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  //border
  const border = get(defaultProperties, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //alignment
  const alignment = get(deviceProperties, 'alignment', {});

  var justifyContent = get(alignment, 'justifyContent', 'left');
  styles['justifyContent'] = justifyContent;

  var flexDirection = get(alignment, 'flexDrection', 'column');
  styles['flexDirection'] = flexDirection;

  var alignItems = get(alignment, 'alignItems', 'start');
  styles['alignItems'] = alignItems;

  var overflow = "visible"
  styles['overflow'] = overflow;

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var borderStyles = {}

  const topLeft = get(value, 'radius.topLeft', null);
  const topRight = get(value, 'radius.topRight', null);
  const bottomLeft = get(value, 'radius.bottomLeft', null);
  const bottomRight = get(value, 'radius.bottomRight', null);

  if (topLeft) {
    borderStyles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    borderStyles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    borderStyles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    borderStyles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  borderStyles['borderColor'] = color;

  const borderWidthString = get(value, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  borderStyles['borderWidth'] = borderWidget;

  return borderStyles;
}

export { FormWidget };